import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom'
import { MessageSquare, Book, Settings, Home } from 'lucide-react'
import SwissVATBotWithAuth from './components/SwissVATBotWithAuth'
import KnowledgeCenter from './components/KnowledgeCenter'
import AdminPDFUpload from './components/AdminPDFUpload'
import AdminKnowledgePanel from './components/AdminKnowledgePanel'

// Simple navigation component
const Navbar = () => {
  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">S</span>
            </div>
            <span className="text-xl font-bold text-gray-900">SwissVAT.ai</span>
          </Link>
          
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="flex items-center gap-2 text-gray-700 hover:text-red-600 transition-colors"
            >
              <Home className="w-4 h-4" />
              <span>Home</span>
            </Link>
            <Link
              to="/chat"
              className="flex items-center gap-2 text-gray-700 hover:text-red-600 transition-colors"
            >
              <MessageSquare className="w-4 h-4" />
              <span>Chat</span>
            </Link>
            <Link
              to="/knowledge"
              className="flex items-center gap-2 text-gray-700 hover:text-red-600 transition-colors"
            >
              <Book className="w-4 h-4" />
              <span>Knowledge</span>
            </Link>
            <Link
              to="/admin"
              className="flex items-center gap-2 text-gray-700 hover:text-red-600 transition-colors"
            >
              <Settings className="w-4 h-4" />
              <span>Admin</span>
            </Link>
          </div>
        </div>
      </div>
    </nav>
  )
}

// Simple home page
const HomePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-4xl mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Swiss VAT Made Simple 🇨🇭
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            AI-powered chatbot that answers all your Swiss VAT questions instantly.
            Backed by official FTA documentation.
          </p>
          <div className="flex gap-4 justify-center">
            <Link
              to="/chat"
              className="px-8 py-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold text-lg"
            >
              Try Free Chatbot
            </Link>
            <Link
              to="/knowledge"
              className="px-8 py-4 border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-semibold text-lg"
            >
              Browse Knowledge Base
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <MessageSquare className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">AI Chatbot</h3>
            <p className="text-gray-600">
              Ask any VAT question and get instant answers with legal citations. Free trial included.
            </p>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <Book className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Knowledge Center</h3>
            <p className="text-gray-600">
              Browse comprehensive guides on VAT registration, rates, compliance, and more.
            </p>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <Settings className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Always Updated</h3>
            <p className="text-gray-600">
              Our knowledge base is regularly updated with the latest FTA regulations.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Admin page with tabs
const AdminPage = () => {
  const [activeTab, setActiveTab] = React.useState('pdfs')

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Admin Panel</h1>
        
        <div className="mb-6 border-b border-gray-200">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('pdfs')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'pdfs'
                  ? 'text-red-600 border-b-2 border-red-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              PDF Management
            </button>
            <button
              onClick={() => setActiveTab('articles')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'articles'
                  ? 'text-red-600 border-b-2 border-red-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Knowledge Articles
            </button>
          </div>
        </div>

        {activeTab === 'pdfs' && <AdminPDFUpload />}
        {activeTab === 'articles' && <AdminKnowledgePanel />}
      </div>
    </div>
  )
}

function App() {
  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/chat" element={<SwissVATBotWithAuth />} />
          <Route path="/knowledge" element={<KnowledgeCenter />} />
          <Route path="/knowledge/:slug" element={<KnowledgeCenter />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </div>
    </Router>
  )
}

export default App
